/**
 * Sparky: Zapboard's little electron buddy. A crisp SVG character with a speech bubble.
 * Every line Sparky says is shown as a caption and (optionally) read aloud.
 */
import { h } from '../ui/dom';
import type { Voice } from './voice';

export type Mood = 'happy' | 'wow' | 'oops' | 'think';

const SPARKY_SVG = `
<svg viewBox="0 0 120 130" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <radialGradient id="spk-body" cx="38%" cy="32%" r="75%">
      <stop offset="0" stop-color="#fff7b0"/>
      <stop offset="0.45" stop-color="#ffd93b"/>
      <stop offset="1" stop-color="#ffa21f"/>
    </radialGradient>
    <radialGradient id="spk-aura" cx="50%" cy="50%" r="50%">
      <stop offset="0.55" stop-color="#ffe066" stop-opacity="0.55"/>
      <stop offset="1" stop-color="#ffe066" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <circle class="spk-aura" cx="60" cy="74" r="56" fill="url(#spk-aura)"/>
  <path class="spk-bolt" d="M66 4 L48 34 L60 34 L52 52 L76 22 L63 22 Z" fill="#ff7a1a" stroke="#fff" stroke-width="3" stroke-linejoin="round"/>
  <g class="spk-arms">
    <path class="spk-arm-l" d="M24 82 q-14 -6 -16 -20" stroke="#ff9d1f" stroke-width="7" stroke-linecap="round" fill="none"/>
    <path class="spk-arm-r" d="M96 82 q14 -6 16 -20" stroke="#ff9d1f" stroke-width="7" stroke-linecap="round" fill="none"/>
  </g>
  <circle cx="60" cy="78" r="40" fill="url(#spk-body)" stroke="#fff" stroke-width="4"/>
  <ellipse cx="44" cy="62" rx="9" ry="5" fill="#fff" opacity="0.55" transform="rotate(-25 44 62)"/>
  <g class="spk-eyes">
    <ellipse cx="46" cy="74" rx="8.5" ry="10.5" fill="#fff"/>
    <ellipse cx="74" cy="74" rx="8.5" ry="10.5" fill="#fff"/>
    <circle class="spk-pupil" cx="48" cy="76" r="5" fill="#2b2250"/>
    <circle class="spk-pupil" cx="76" cy="76" r="5" fill="#2b2250"/>
    <circle cx="50" cy="73" r="1.8" fill="#fff"/>
    <circle cx="78" cy="73" r="1.8" fill="#fff"/>
  </g>
  <ellipse cx="36" cy="90" rx="6" ry="3.5" fill="#ff7aa8" opacity="0.6"/>
  <ellipse cx="84" cy="90" rx="6" ry="3.5" fill="#ff7aa8" opacity="0.6"/>
  <path class="spk-mouth spk-mouth-smile" d="M50 92 q10 10 20 0" stroke="#2b2250" stroke-width="4" stroke-linecap="round" fill="none"/>
  <ellipse class="spk-mouth spk-mouth-open" cx="60" cy="95" rx="7" ry="6" fill="#2b2250"/>
  <path class="spk-mouth spk-mouth-oops" d="M51 97 q9 -7 18 0" stroke="#2b2250" stroke-width="4" stroke-linecap="round" fill="none"/>
</svg>`;

export class Sparky {
  readonly el: HTMLElement;
  private bubble: HTMLElement;
  private text: HTMLElement;
  private hideTimer = 0;
  private talkTimer = 0;
  /** Called when Sparky is tapped (e.g. to repeat or give a hint). */
  onTap: () => void = () => {};
  /**
   * When set (phones), Sparky's words are shown by the host's coach card instead of the
   * floating bubble: (text, mood, how long to show it, when he stops talking).
   */
  onSay: ((text: string, mood: Mood, holdMs: number) => void) | null = null;
  onQuiet: (() => void) | null = null;

  constructor(
    parent: HTMLElement,
    private voice: Voice,
  ) {
    this.text = h('span.spk-text');
    const replay = h(
      'button.spk-replay',
      {
        'aria-label': 'Say it again',
        title: 'Say it again',
        onclick: (e: Event) => {
          e.stopPropagation();
          this.repeat();
        },
      },
      '🔊',
    );
    this.bubble = h('div.spk-bubble', { role: 'status', 'aria-live': 'polite' }, this.text, replay);
    const body = h('button.spk-body', {
      'aria-label': 'Sparky, your helper',
      html: SPARKY_SVG,
      onclick: () => this.onTap(),
    });
    this.el = h('div.sparky.mood-happy', {}, this.bubble, body);
    parent.append(this.el);
  }

  private last = '';

  say(text: string, mood: Mood = 'happy', opts: { speak?: boolean; hold?: number } = {}) {
    this.last = text;
    this.text.textContent = text;
    this.el.className = `sparky mood-${mood} talking show`;
    this.bubble.classList.remove('pop');
    void this.bubble.offsetWidth; // restart the pop animation
    this.bubble.classList.add('pop');
    window.clearTimeout(this.hideTimer);
    window.clearTimeout(this.talkTimer);
    const words = text.split(/\s+/).length;
    const readMs = Math.max(2600, words * 420);
    const stopTalking = () => {
      this.el.classList.remove('talking');
      this.onQuiet?.();
    };
    if (opts.speak !== false) this.voice.say(text, stopTalking);
    this.talkTimer = window.setTimeout(stopTalking, Math.min(readMs, words * 330 + 600));
    if (this.onSay) {
      // The host shows the words in its own card (phones): no floating bubble at all.
      this.el.classList.remove('show');
      this.onSay(text, mood, opts.hold ?? readMs + 900);
      return;
    }
    this.hideTimer = window.setTimeout(() => this.el.classList.remove('show'), opts.hold ?? readMs + 2500);
  }

  repeat() {
    if (this.last) this.say(this.last, 'happy');
  }

  hide() {
    this.el.classList.remove('show', 'talking');
  }

  /** Little celebratory jump. */
  cheer() {
    this.el.classList.remove('cheer');
    void this.el.offsetWidth;
    this.el.classList.add('cheer');
  }
}
